import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InfoCard, StructuredMetadataTable } from '@backstage/core-components';
import { Box } from '@mui/material';
import { Alert } from '@material-ui/lab';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { omit, pick } from 'lodash';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ReactJson from 'react-json-view'
import { Chip, Grid } from '@material-ui/core';
import { ArgusApp, ArgusCluster, ArgusEnv, ArgusEvent, ArgusStack, ArgusStackEnriched } from '@eng-portal/plugin-argus-common';
import { ClusterCard } from './ClusterCard';
import { StackCard } from './StackCard';
import { AppCard } from './AppCard';
import { EnvCard } from './EnvCard';
import { useAsync } from 'react-use';
import { argusApiRef } from '@eng-portal/plugin-argus-common/src/argusApiRef';
import { useApi } from '@backstage/core-plugin-api';

type ArgusEventCardProps = {
  event: ArgusEvent
  useUtc?: boolean
};

export function ArgusEventCard({ event, useUtc }: ArgusEventCardProps): JSX.Element {
  const { event_type, actor, raw, app, env, stack, cluster } = event;

  const argusApi = useApi(argusApiRef);
  const [stackEnriched, setStackEnriched] = React.useState<ArgusStackEnriched | null>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const processedEvent: any = pick(event, ['id', 'description', 'url']);
  processedEvent.source = `${event.source_system} / ${event.source_id}`;

  const processedApp = omit(JSON.parse(app || '{}'), 'edges') as ArgusApp;
  const processedEnv = omit(JSON.parse(env || '{}'), 'edges') as ArgusEnv;
  const processedCluster = omit(JSON.parse(cluster || '{}'), 'edges') as ArgusCluster;
  const processedStack = omit(JSON.parse(stack || '{}'), 'edges') as ArgusStack;

  useAsync(async () => {
    if (stack && isExpanded) {
      const stackInfo = await argusApi.getStackInfo(processedApp.name, processedEnv.name, processedStack.name);
      setStackEnriched({
        stack: processedStack,
        info: stackInfo,
      });
    }
  }, [processedApp.name, processedEnv.name, stack, isExpanded]);

  return (
    <Accordion
      sx={{ padding: 1}}
      onChange={(_, expanded) => setIsExpanded(expanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
      >
        <Grid container direction="column">
          <Grid item>
            <Typography paddingLeft={1}>{event_type}</Typography>
          </Grid>
          <Grid container spacing={0} direction="row" style={{ paddingLeft: 15 }}>
            {app && Object.keys(processedApp).length > 0 && (
              <Grid item>
                <Chip label={`App: ${processedApp.name}`} />
              </Grid>
            )}
            {env && Object.keys(processedEnv).length > 0 && (
              <Grid item>
                <Chip label={`Env: ${processedEnv.name}`} />
              </Grid>
            )}
            {stack && processedStack?.name !== undefined && (
              <Grid item>
                <Chip label={`Stack: ${processedStack.name}`} />
              </Grid>
            )}
            {cluster && Object.keys(processedCluster).length > 0 && (
              <Grid item>
                <Chip label={`Cluster: ${processedCluster.name}`} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </AccordionSummary>
      {isExpanded && (
        <AccordionDetails>
        {event.message && (
          <Alert severity="warning" style={{ marginBottom: 16 }}>
            {event.message}
          </Alert>
        )}
        <Grid container spacing={2} direction="row">
          <Grid item xs={12} xl={6}>
            <InfoCard title="Metadata" headerProps={{ avatar: <SummarizeIcon /> }}>
              <StructuredMetadataTable metadata={processedEvent} />
            </InfoCard>
          </Grid>
          <Grid item xs={12} xl={6}>
            <InfoCard title="Actor" headerProps={{ avatar: <PermIdentityIcon /> }}>
              <StructuredMetadataTable metadata={JSON.parse(actor)} />
            </InfoCard>
          </Grid>
          {app && Object.keys(processedApp).length > 0 && (
            <Grid item xs={12} xl={6}>
              <AppCard app={processedApp} useUtc={useUtc} />
            </Grid>
          )}
          {env && Object.keys(processedEnv).length > 0 && (
            <Grid item xs={12} xl={6}>
              <EnvCard env={processedEnv} useUtc={useUtc} />
            </Grid>
          )}
          {stackEnriched && Object.keys(stackEnriched?.stack).length > 0 && (
            <Grid item xs={12} xl={6}>
              <StackCard stack={stackEnriched} useUtc={useUtc} appName={processedApp.name} envName={processedEnv.name} />
            </Grid>
          )}
          {cluster && Object.keys(processedCluster).length > 0 && (
            <Grid item xs={12} xl={6}>
              <ClusterCard cluster={processedCluster} useUtc={useUtc} />
            </Grid>
          )}
          <Grid item xs={12}>
            <InfoCard title="Raw Event Data" headerProps={{ avatar: <DataObjectIcon /> }}>
              <Box sx={{backgroundColor: 'white', padding: 2, width: '100%'}}>
                <ReactJson name={null} src={JSON.parse(raw)} collapsed={true} />
              </Box>
            </InfoCard>
          </Grid>
        </Grid>
      </AccordionDetails>
      )}
    </Accordion>
  );
}
